import Graphic from '@arcgis/core/Graphic';
import WebMap from '@arcgis/core/WebMap';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Feature from '@arcgis/core/widgets/Feature';
import { geojsonToArcGIS } from '@terraformer/arcgis';

import type { WebMap as WebMapConfigType } from '@/common/overmind/namespaces/arcgis/config/config.types';
import type { GridElementRelatedFeatureLayer2 } from '@/features/execute-calculation-set/components/Calculation/GridElementsCalculationTacticResults/useTitle';
import { queryGridElementsDetailsByGlobalID2 } from '@/features/execute-calculation-set/components/Calculation/GridElementsCalculationTacticResults/useTitle';
import type { DomainGuid } from '@/features/execute-calculation-set/overmind/calculation';

import type { FeatureWithIDs } from '../baseadapter';

export async function getTitlesFromWebMap(
  featuresWithIds: FeatureWithIDs<unknown>[],
  webMap: WebMap
) {
  const featuresByLayers = webMap.allLayers
    .filter(layer => (layer as any).layerId != null)
    .map(layer => ({
      globalIds: featuresWithIds
        .filter(f => f.gc.layerId === (layer as any).layerId)
        .map(f => f.gc.globalId),
      layer
    }))
    .filter(f => f.globalIds.length > 0)
    .toArray();

  return queryGridElementsDetailsByGlobalID2(featuresByLayers).then(
    matchingFeatures => {
      const matchingLayers = matchingFeatures.filter(
        featureSet => featureSet.features.length > 0
      );

      const featureLayerFromWebMap: GridElementRelatedFeatureLayer2 = {
        type: 'LOADED',
        layer: matchingLayers
          .flatMap(set => set.features)
          // TODO: ELVIA SPECIFIC - REMOVE AFTER DEMO!!!!!!!
          .filter(
            feature =>
              feature.attributes.globalid || feature.attributes.GLOBALID
          )
          .reduce((acc, feature) => {
            // ELVIA DEMO - FIX AFTER!!!!!!!!!!!!
            const globalId = (feature.attributes.globalid ||
              feature.attributes.GLOBALID) as DomainGuid;
            const layer = feature.layer as __esri.FeatureLayer;
            acc[globalId] = layer;
            return acc;
          }, {} as Record<DomainGuid, __esri.FeatureLayer>)
      };

      const gridElements = featuresWithIds.map(m => ({
        globalId: m.gc.globalId,
        objectId: m.id as number,
        geometry: geojsonToArcGIS(m) as Graphic
      }));

      const titles = gridElements.map((gridElement, index) => {
        const abortController = new AbortController();
        const graphic = new Graphic({
          attributes: gridElement.geometry.attributes,
          geometry: gridElement.geometry.geometry,
          layer: featureLayerFromWebMap.layer[gridElement.globalId]
        });

        const feature = new Feature({
          graphic,
          visibleElements: {
            title: false
          }
        });
        setTimeout(() => {
          abortController.abort();
        }, 500);

        return reactiveUtils
          .once(() => feature.title, abortController.signal)
          .then(title => {
            const titleByFeature: TitleByFeature = {
              [gridElement.globalId]: title
            };
            return titleByFeature;
          });
      });

      return Promise.allSettled(titles)
        .then(results => {
          return results
            .filter(r => r.status === 'fulfilled')
            .map(r => r.value);
        })
        .then(gridElementTitles => {
          return gridElementTitles.reduce((acc, titleByFeature) => {
            return { ...acc, ...titleByFeature };
          }, {} as TitleByFeature);
        });
    }
  );
}

export function getBasemapSpatialReference(webMap: __esri.WebMap) {
  if (webMap.basemap.spatialReference) return webMap.basemap.spatialReference;
  if (webMap.basemap.baseLayers.length === 0) {
    console.error('No baselayer found in webmap');

    return;
  }

  return (
    webMap.basemap.baseLayers.at(0) as __esri.VectorTileLayer | __esri.TileLayer
  ).spatialReference;
}

export type TitleByFeature = Record<DomainGuid, string>;

type UnloadedWebMap = { load: () => Promise<WebMap> };

let webmapProxy: WebMap;
export function getWebMap(webMapConfig: WebMapConfigType): UnloadedWebMap {
  const webmap = new WebMap({
    portalItem: {
      id: webMapConfig.id,
      portal: {
        url: webMapConfig.url
      }
    }
  });
  if (!webmapProxy) {
    webmapProxy = createDebugProxy(
      webmap,
      'WebMap',
      removeBrokenLayer,
      true
    ) as unknown as WebMap;
  }

  return {
    async load() {
      await webmapProxy.loadAll();

      return webmapProxy;
    }
  };
}

export type GetTitlesForFeatures = (
  featuresWithIds: FeatureWithIDs<unknown>[]
) => Promise<TitleByFeature>;
export const createDebugProxy = (
  target,
  name = 'DebugTarget',
  onError?: (error: any, target: any) => void,
  onErrorRethrows = false
) => {
  if (!onError) {
    onError = onErrorDefault;
  }
  return new Proxy(target, {
    get(target, property) {
      const originalValue = target[property];

      // Only intercept functions
      if (typeof originalValue === 'function') {
        return function (...args) {
          const functionName = `${name}.${property}`;

          console.group(functionName);
          console.log('⏳ Starting:', functionName);
          console.log('📥 Arguments:', args);
          withDebugger(createDebugProxy);

          try {
            const startTime = performance.now();
            const result = originalValue.apply(target, args);

            // Handle both Promise and regular returns
            if (result instanceof Promise) {
              return result
                .then(resolvedValue => {
                  const endTime = performance.now();
                  withDebugger(createDebugProxy);
                  console.log('📤 Return value:', resolvedValue);
                  console.log(
                    `⏱️ Execution time: ${(endTime - startTime).toFixed(2)}ms`
                  );
                  console.log('✅ Completed:', functionName);
                  console.groupEnd();
                  return resolvedValue;
                })
                .catch(error => {
                  withDebugger(createDebugProxy);
                  onError(error, target);
                });
            } else {
              const endTime = performance.now();
              withDebugger(createDebugProxy);
              console.log('📤 Return value:', result);
              console.log(
                `⏱️ Execution time: ${(endTime - startTime).toFixed(2)}ms`
              );
              console.log('✅ Completed:', functionName);
              console.groupEnd();
              return result;
            }
          } catch (error) {
            withDebugger(createDebugProxy);
            onError(error, target);
          }
        };
      }

      return originalValue;
    }
  });
  function withDebugger(proxifier) {
    if (proxifier.debugging) {
      debugger;
    }
  }

  function onErrorDefault(error, target) {
    console.error('❌ Error:', error);
    console.groupEnd();
    if (onErrorRethrows) {
      throw error;
    }
  }
};

const findBrokenLayerIndex = (target, brokenLayerUrl) =>
  target.layers.items.findIndex(i =>
    i.layers.items.some(i => i.parsedUrl.path === brokenLayerUrl)
  );

const removeBrokenLayer = (error, target) => {
  console.error('❌ Error: ', error);
  const brokenLayerIndex = findBrokenLayerIndex(target, error.details.url);
  target.layers.removeAt(brokenLayerIndex);
};

// @ts-ignore
window.createDebugProxy = createDebugProxy;
